import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { faSchoolFlag, faPersonChalkboard, faPeopleGroup, faPrint, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

import { ManagementUser } from '../../../core/models/management-user.model';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { ReportsService } from '../../../core/services/reports.service';
import { LoadingService } from '../../../core/services/loading.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { canChangeSchool, canViewSchoolFilter } from '../../utils';

interface RankedSchool {
  name: string ;
  value: number ;
}

@Component({
    selector: 'wf-district-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: false
})
export class DistrictDashboardComponent implements OnInit {
  currentUser: ManagementUser | null = null ;
  showSchoolFilter: boolean = false ;
  shouldShowSchools: boolean = false ;
  schoolFilterError: string = '' ;
  loadError: string = '' ;

  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  selectedDistrictId: number = -1 ;
  selectedSchoolId: number = 0 ;

  reportData: any ;
  rangeData: any ;
  dateRanges: any[] = [] ;
  dateRange: any = {} ;

  // --- Top summary cards ---
  numSchools: number = 0 ;
  numTeachers: number = 0 ;
  numStudentsEnrolled: number = 0 ;

  // --- School Engagement ---
  schoolsMeetingGoal: number = 0 ;
  totalSchools: number = 0 ;
  topSchoolEngagement: RankedSchool[] = [] ;

  // --- Teacher Usage ---
  teacherLoginPercent: number = 0 ;
  topTeacherLogins: RankedSchool[] = [] ;

  // --- Student Engagement (donut) ---
  // Buckets, ordered to match the mockup key: 80+, 60-79, 40-59, 20-39, 0-19
  studentMinutePercents: number[] = [ 0, 0, 0, 0, 0 ] ;
  studentsGetting60Plus: number = 0 ;
  topStudentUsage: RankedSchool[] = [] ;

  // --- Student Units ---
  studentUnitsCount: number = 0 ;
  studentUnitsPercent: number = 0 ;

  // Icons
  schoolsIcon = faSchoolFlag ;
  teachersIcon = faPersonChalkboard ;
  studentsIcon = faPeopleGroup ;
  printIcon = faPrint ;
  helpIcon = faQuestionCircle ;

  // Donut chart (Student Engagement)
  donutType: 'doughnut' = 'doughnut' ;
  donutColors: string[] = [ '#B2D235', '#0095DA', '#007236', '#8CCEEE', '#555555' ] ;
  donutData: ChartData<'doughnut'> = {
    labels: [ '80+ minutes', '60-79 minutes', '40-59 minutes', '20-39 minutes', '0-19 minutes' ],
    datasets: [
      {
        data: [],
        backgroundColor: this.donutColors,
        borderWidth: 0,
      }
    ]
  } ;
  donutOpts: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '62%',
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
  } ;

  // Half-moon gauges (School Engagement & Teacher Usage)
  gaugeType: 'doughnut' = 'doughnut' ;
  gaugeOpts: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    rotation: -90,
    circumference: 180,
    cutout: '70%',
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: { enabled: false },
    },
  } ;
  schoolGaugeData: ChartData<'doughnut'> = {
    datasets: [ { data: [ 0, 1 ], backgroundColor: [ '#0054A6', '#DFDFDF' ], borderWidth: 0 } ]
  } ;
  teacherGaugeData: ChartData<'doughnut'> = {
    datasets: [ { data: [ 0, 1 ], backgroundColor: [ '#80C342', '#DFDFDF' ], borderWidth: 0 } ]
  } ;

  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective> ;

  constructor(
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private loadingService: LoadingService,
    private sessionStorageService: SessionStorageService,
  ) { }

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser) ;
    this.shouldShowSchools = this.currentUser ? canChangeSchool(this.currentUser) : false ;

    // Resolve data (reuses the District Usage report data)
    this.reportData = this.route.snapshot.data['resolveData'] ;
    this.rangeData = this.reportData.rangeData.schools ;
    this.dateRanges = this.reportData.ranges ;
    // Default to the most recent week so per-week metrics (e.g. units/week) present a meaningful
    // weekly snapshot rather than a full-year average that can never reach one unit per week.
    this.dateRange = this.defaultDateRange() ;

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts.filter((district: District) => district.name !== 'Clever District') ;
    this.schools = this.route.snapshot.data['filterData'].schools.filter((school: School) => school.name !== 'Clever School') ;
    this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;
    this.selectedDistrictId = this.reportsService.getSelectedDistrictForReports().districtID ;
    this.selectedSchoolId = this.reportsService.getSelectedSchoolForReports().schoolID ;

    // Load the most recent week's data for the initial view (falls back to the resolved
    // all-ranges data if this district has no weekly ranges yet).
    if (this.dateRanges.length)
    {
      this.loadRangeData() ;
    }
    else
    {
      this.computeDashboard(this.reportsService.filterDistrictReportData(this.rangeData, this.selectedSchoolId)) ;
    }
  }

  filterSchoolTeacher(filterOpts: any) {
    if (filterOpts.district.districtID !== this.selectedDistrictId)
    {
      // District was changed, so we need to load new report data
      this.selectedDistrictId = filterOpts.district.districtID ;
      this.selectedSchoolId = 0 ;
      this.loadError = '' ;
      this.loadingService.start() ;
      this.reportsService.getDistrictUsageData().subscribe({
        next: (reportData: any) => {
          this.reportData = reportData ;
          this.rangeData = reportData.rangeData.schools ;
          this.dateRanges = reportData.ranges ;
          // Default to the most recent week for the newly selected district as well.
          this.dateRange = this.defaultDateRange() ;
          if (this.dateRanges.length)
          {
            this.loadRangeData() ;
          }
          else
          {
            this.computeDashboard(this.rangeData) ;
          }
          this.loadingService.finish() ;
        },
        error: () => {
          this.loadError = 'There was an error while trying to load data for the selected district. Please try again, if the problem continues please contact us for help.' ;
          this.loadingService.finish() ;
        }
      }) ;
    }
    else
    {
      // School updated
      this.selectedSchoolId = filterOpts.school.schoolID ;
      let filteredData: any = this.reportsService.filterDistrictReportData(this.rangeData, this.selectedSchoolId) ;
      this.computeDashboard(filteredData) ;
    }
  }

  loadRangeData() {
    this.loadError = '' ;
    this.loadingService.start() ;
    this.reportsService.getDistrictUsageRangeData(this.selectedDistrictId, this.dateRange.start, this.dateRange.end).subscribe({
      next: (rangeData: any) => {
        this.rangeData = rangeData.schools ;
        let filteredData = this.reportsService.filterDistrictReportData(this.rangeData, this.selectedSchoolId) ;
        this.computeDashboard(filteredData) ;
        this.loadingService.finish() ;
      },
      error: () => {
        this.loadError = 'There was an error while trying to load data for the selected date range. Please try again, if the problem continues please contact us for help.' ;
        this.loadingService.finish() ;
      }
    }) ;
  }

  /**
   * Derive the district-level summary metrics from the (filtered) range data.
   * NOTE: Several of these metrics mirror the Usage report. The exact district
   * "engagement" definitions are still being finalized with the backend, so a
   * few values are computed from the closest available data for now.
   */
  computeDashboard(data: any): void {
    let schools = Object.values(data) ;
    let teacherCount = 0 ;
    let enrolled = 0 ;

    let totalStudents = 0 ;
    let studentsWithLogin = 0 ;
    let studentsWithUnit = 0 ;
    let schoolsMeetingGoal = 0 ;
    let bins = [ 0, 0, 0, 0, 0 ] ; // 0-19, 20-39, 40-59, 60-79, 80+

    let schoolEngagement: RankedSchool[] = [] ;
    let schoolUnits: RankedSchool[] = [] ;
    let teacherLoginsBySchool: RankedSchool[] = [] ;

    schools.forEach((school: any) => {
      let schoolTeachers = Object.values(school.teachers) ;
      teacherCount += schoolTeachers.length ;

      let schoolStudents = 0 ;
      let schoolMeetingMin = 0 ;
      let schoolLogins = 0 ;

      schoolTeachers.forEach((teacher: any) => {
        schoolStudents += teacher.totalStudents ;
        schoolMeetingMin += teacher.totalStudentsMeetingMin ;
        schoolLogins += teacher.numberOfLogins || 0 ;

        Object.values(teacher.students).forEach((student: any) => {
          let usage = student.usage ;
          if (usage < 20) bins[0]++ ;
          else if (usage < 40) bins[1]++ ;
          else if (usage < 60) bins[2]++ ;
          else if (usage < 80) bins[3]++ ;
          else bins[4]++ ;

          totalStudents++ ;
          enrolled++ ;
          if (student.sessionCount > 0) studentsWithLogin++ ;
          // "Students getting at least one unit per week" — meetingUnitGoal is the backend's
          // single rule: averaging >= 1 unit/week over the range, OR having reached the final
          // curriculum unit (a completer has no further units left to progress through).
          if (student.meetingUnitGoal) studentsWithUnit++ ;
        }) ;
      }) ;

      let schoolMeetingPercent = (schoolStudents === 0) ? 0 : Math.round((schoolMeetingMin / schoolStudents) * 100) ;
      if (schoolMeetingPercent >= 60) schoolsMeetingGoal++ ;

      schoolEngagement.push({ name: school.name, value: schoolMeetingPercent }) ;
      schoolUnits.push({ name: school.name, value: schoolMeetingPercent }) ;
      teacherLoginsBySchool.push({ name: school.name, value: schoolLogins }) ;
    }) ;

    // Top cards
    this.numSchools = schools.length ;
    this.numTeachers = teacherCount ;
    this.numStudentsEnrolled = enrolled ;

    // School Engagement
    this.totalSchools = schools.length ;
    this.schoolsMeetingGoal = schoolsMeetingGoal ;
    this.topSchoolEngagement = this.topThree(schoolEngagement) ;
    this.schoolGaugeData.datasets[0].data = [ schoolsMeetingGoal, Math.max(schools.length - schoolsMeetingGoal, 0) ] ;

    // Teacher Usage
    // TODO: "% teachers with at least one login per week" is not yet available in
    // the usage data; placeholder until the district engagement metrics land.
    this.teacherLoginPercent = 0 ;
    this.topTeacherLogins = this.topThree(teacherLoginsBySchool) ;
    this.teacherGaugeData.datasets[0].data = [ this.teacherLoginPercent, Math.max(100 - this.teacherLoginPercent, 0) ] ;

    // Student Engagement donut: reorder bins to 80+, 60-79, 40-59, 20-39, 0-19
    let ordered = [ bins[4], bins[3], bins[2], bins[1], bins[0] ] ;
    this.studentMinutePercents = ordered.map((count) => (totalStudents === 0) ? 0 : Math.round((count / totalStudents) * 100)) ;
    this.studentsGetting60Plus = this.studentMinutePercents[0] + this.studentMinutePercents[1] ;
    this.donutData.datasets[0].data = ordered ;
    this.topStudentUsage = this.topThree(schoolUnits) ;

    // Student Units
    this.studentUnitsCount = studentsWithUnit ;
    this.studentUnitsPercent = (totalStudents === 0) ? 0 : Math.round((studentsWithUnit / totalStudents) * 100) ;

    if (this.charts) this.charts.forEach((chart: BaseChartDirective) => chart.update()) ;
  }

  /**
   * The week to show by default: the most recent range that actually has usage. weeklyUsage is
   * parallel to the ranges array, so we walk back from the newest week to skip trailing empty
   * weeks (e.g. summer break) and land on the latest week with real activity.
   */
  private defaultDateRange(): any {
    if (!this.dateRanges.length) return { startDisplay: '-', endDisplay: '-' } ;
    let weekly: number[] = this.reportData?.weeklyUsage ?? [] ;
    for (let i = this.dateRanges.length - 1 ; i >= 0 ; i--) {
      if ((weekly[i] ?? 0) > 0) return this.dateRanges[i] ;
    }
    return this.dateRanges[this.dateRanges.length - 1] ;
  }

  private topThree(items: RankedSchool[]): RankedSchool[] {
    return [ ...items ].sort((a, b) => b.value - a.value).slice(0, 3) ;
  }
}
